/* eslint-disable */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, AutoFixHigh as AutoFixHighIcon } from "@mui/icons-material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { GET_CONTENT_STRATEGIES } from "../../gql/queries/ai";
import { CREATE_CONTENT_STRATEGY, DELETE_CONTENT_STRATEGY } from "../../gql/mutations/ai";
import AiPageHeader from "./AiPageHeader";

const SectionWrapper = styled("section")(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  backgroundColor: theme.palette.background.default,
  paddingBottom: "60px",
}));

function AiContentStrategy() {
  const theme = useTheme();
  const userData = useSelector(state => state.reducer.userData);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") || "";

  const [localProjectId, setLocalProjectId] = useState(projectId || "");
  const [seedKeyword, setSeedKeyword] = useState("");
  const [expandedCard, setExpandedCard] = useState(null);

  const [fetchStrategies, { data: strategiesData, loading: strategiesLoading }] = useLazyQuery(GET_CONTENT_STRATEGIES, { fetchPolicy: "network-only" });

  const [createStrategy, { loading: createLoading }] = useMutation(CREATE_CONTENT_STRATEGY, {
    onCompleted: () => {
      toast.success("Content strategy created successfully!");
      setSeedKeyword("");
      handleFetchStrategies();
    },
    onError: err => {
      toast.error(err.message || "Failed to create strategy");
    },
  });

  const [deleteStrategy, { loading: deleteLoading }] = useMutation(DELETE_CONTENT_STRATEGY, {
    onCompleted: () => {
      toast.success("Strategy deleted successfully!");
      handleFetchStrategies();
    },
    onError: err => {
      toast.error(err.message || "Failed to delete strategy");
    },
  });

  const activeProjectId = projectId || localProjectId;

  const handleFetchStrategies = () => {
    if (activeProjectId) {
      fetchStrategies({ variables: { projectId: activeProjectId } });
    }
  };

  useEffect(() => {
    if (projectId) setLocalProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (activeProjectId) {
      handleFetchStrategies();
    }
  }, [activeProjectId]);

  const handleCreate = () => {
    if (!activeProjectId) {
      toast.error("Please provide a Project ID");
      return;
    }
    if (!seedKeyword.trim()) {
      toast.error("Please enter a seed keyword");
      return;
    }
    createStrategy({
      variables: {
        data: {
          projectId: activeProjectId,
          seedKeyword: seedKeyword.trim(),
        },
      },
    });
  };

  const handleDelete = id => {
    deleteStrategy({ variables: { id } });
  };

  const toggleExpand = id => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const statusConfig = {
    draft: { color: "default", label: "Draft" },
    in_progress: { color: "info", label: "In Progress" },
    completed: { color: "success", label: "Completed" },
  };

  const getStatusChip = status => {
    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} size="small" color={config.color} />;
  };

  const parseJSON = value => {
    try {
      return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
      return value;
    }
  };

  const formatDate = dateStr => {
    if (!dateStr) return "---";
    try {
      return new Date(Number(dateStr) || dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const strategies = strategiesData?.getContentStrategies || [];

  return (
    <SectionWrapper>
      <Container maxWidth="">
        <Box sx={{ flexGrow: 1 }}>
          <AiPageHeader
            title="Content Strategy Planner"
            description="Create AI-powered content strategies with topic clusters, pillar pages, and publish schedules."
            projectId={activeProjectId}
            onProjectChange={id => setLocalProjectId(id || "")}
            showProjectSelector
            showQuickLinks
          />

          {/* Controls */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <Card
                sx={{
                  padding: "20px",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "none",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Seed Keyword
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Enter a seed keyword (e.g. 'technical SEO')"
                      value={seedKeyword}
                      onChange={e => setSeedKeyword(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") handleCreate();
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, visibility: "hidden" }}>
                      Action
                    </Typography>
                    <Button variant="contained" onClick={handleCreate} disabled={createLoading || !activeProjectId} sx={{ textTransform: "none", minWidth: 160 }}>
                      {createLoading ? <CircularProgress size={22} color="inherit" /> : "Create Strategy"}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Strategies List */}
          <Grid container spacing={2} mt={1}>
            {strategiesLoading ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              </Grid>
            ) : strategies.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <AutoFixHighIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No content strategies yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeProjectId
                      ? "Enter a seed keyword above and click Create Strategy to plan your content cluster."
                      : "Select a website above to create content strategies for that project."}
                  </Typography>
                  {activeProjectId && (
                    <Button variant="contained" onClick={handleCreate} disabled={createLoading || !seedKeyword.trim()} sx={{ textTransform: "none" }}>
                      Create Strategy
                    </Button>
                  )}
                </Box>
              </Grid>
            ) : (
              strategies.map(strategy => {
                const topics = parseJSON(strategy.topics);
                const pillarPages = parseJSON(strategy.pillarPages);
                const clusterMap = parseJSON(strategy.clusterMap);
                const progress = strategy.totalPieces > 0 ? Math.round((strategy.completedPieces / strategy.totalPieces) * 100) : 0;

                return (
                  <Grid item xs={12} md={6} lg={4} key={strategy.id}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "none",
                        cursor: "pointer",
                        transition: "border-color 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onClick={() => toggleExpand(strategy.id)}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1.5,
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {strategy.seedKeyword}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {getStatusChip(strategy.status)}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={e => {
                                e.stopPropagation();
                                handleDelete(strategy.id);
                              }}
                              disabled={deleteLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Progress
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {strategy.completedPieces || 0}/{strategy.totalPieces || 0} pieces
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          Created: {formatDate(strategy.createdAt)}
                        </Typography>

                        {/* Expanded Details */}
                        <Collapse in={expandedCard === strategy.id} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {/* Topics */}
                            {topics && Array.isArray(topics) && topics.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                  Topics
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {topics.map((topic, idx) => (
                                    <Chip
                                      key={idx}
                                      label={typeof topic === "string" ? topic : topic.title || topic.name || JSON.stringify(topic)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Pillar Pages */}
                            {pillarPages && Array.isArray(pillarPages) && pillarPages.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                  Pillar Pages
                                </Typography>
                                {pillarPages.map((page, idx) => (
                                  <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                    {typeof page === "string" ? page : page.title || page.url || JSON.stringify(page)}
                                  </Typography>
                                ))}
                              </Box>
                            )}

                            {/* Cluster Map */}
                            {clusterMap && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                  Cluster Map
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    fontSize: "12px",
                                    backgroundColor: theme.palette.mode === "dark" ? "#FFFFFF0A" : "#F5F5F5",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    maxHeight: 200,
                                    overflow: "auto",
                                  }}
                                >
                                  {typeof clusterMap === "string" ? clusterMap : JSON.stringify(clusterMap, null, 2)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 1,
                          }}
                        >
                          {expandedCard === strategy.id ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>
      </Container>
    </SectionWrapper>
  );
}

export default AiContentStrategy;
